// @ts-nocheck
import { forwardRef, useRef } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  Menu,
  ActionIcon,
  useMantineTheme,
  useComputedColorScheme,
} from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card } from '../../../ui';
import { FadeIn } from '../../../animations';
import { exportChart } from './utils/exportChart';
import type { BarChartWidgetProps } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

const BarChartWidget = forwardRef<HTMLDivElement, BarChartWidgetProps>(
  (
    {
      title,
      subtitle,
      data,
      bars,
      orientation = 'vertical',
      stacked = false,
      height = 300,
      showGrid = true,
      showLegend = true,
      showExport = true,
      onExport,
      xAxisKey = 'category',
      xAxisLabel,
      yAxisLabel,
      className,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const chartRef = useRef<HTMLDivElement>(null);
    const isDark = colorScheme === 'dark';
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const handleExport = async (format: 'png' | 'svg' | 'csv') => {
      if (onExport) {
        onExport({ format, filename: `${title?.toLowerCase().replace(/\s+/g, '-')}-chart` });
      } else if (chartRef.current) {
        await exportChart(chartRef.current, data, {
          format,
          filename: `${title?.toLowerCase().replace(/\s+/g, '-')}-chart`,
        });
      }
    };

    const layout = orientation === 'horizontal' ? 'horizontal' : 'vertical';

    return (
      <FadeIn>
        <Card ref={ref} withBorder p="lg" className={className}>
          <Stack gap="md">
            <Group justify="space-between" align="flex-start">
              <Box>
                {title && <Text size="lg" fw={600} style={accessibilityStyles}>{title}</Text>}
                {subtitle && <Text size="sm" c="dimmed" style={accessibilityStyles}>{subtitle}</Text>}
              </Box>

              {showExport && (
                <Menu shadow="md" width={150}>
                  <Menu.Target>
                    <ActionIcon variant="light" size="lg">
                      <IconDownload size={18} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>Export</Menu.Label>
                    <Menu.Item onClick={() => handleExport('png')}>Export as PNG</Menu.Item>
                    <Menu.Item onClick={() => handleExport('svg')}>Export as SVG</Menu.Item>
                    <Menu.Item onClick={() => handleExport('csv')}>Export as CSV</Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>

            <Box ref={chartRef} style={{ width: '100%', height }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data}
                  layout={layout}
                  margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                >
                  {showGrid && (
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={isDark ? theme.colors.dark[4] : theme.colors.gray[3]}
                    />
                  )}

                  <XAxis
                    type={layout === 'horizontal' ? 'number' : 'category'}
                    dataKey={layout === 'vertical' ? xAxisKey : undefined}
                    label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -5 } : undefined}
                    stroke={isDark ? theme.colors.dark[2] : theme.colors.gray[6]}
                    tick={{ fill: isDark ? theme.colors.dark[2] : theme.colors.gray[6], fontSize: 12 }}
                  />

                  <YAxis
                    type={layout === 'horizontal' ? 'category' : 'number'}
                    dataKey={layout === 'horizontal' ? xAxisKey : undefined}
                    label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
                    stroke={isDark ? theme.colors.dark[2] : theme.colors.gray[6]}
                    tick={{ fill: isDark ? theme.colors.dark[2] : theme.colors.gray[6], fontSize: 12 }}
                  />

                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDark ? theme.colors.dark[6] : theme.white,
                      border: `1px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[3]}`,
                      borderRadius: theme.radius.sm,
                    }}
                  />

                  {showLegend && <Legend />}

                  {bars.map((bar) => (
                    <Bar
                      key={bar.dataKey}
                      dataKey={bar.dataKey}
                      name={bar.name}
                      fill={bar.color}
                      stackId={stacked ? bar.stackId || 'stack' : undefined}
                      animationDuration={1000}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Stack>
        </Card>
      </FadeIn>
    );
  }
);

BarChartWidget.displayName = 'BarChartWidget';

export default BarChartWidget;
